import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X, Landmark, TrendingDown, Play, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany } from "@/lib/company";
import { useAuth } from "@/lib/auth";

const fmt = (v, cur = "INR") => {
  const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" }[cur] || (cur + " ");
  return `${sym}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function AssetModal({ open, initial, onClose, onSaved }) {
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", code: "", category: "General",
    branch_id: "", project_id: "",
    acquisition_date: new Date().toISOString().slice(0, 10),
    acquisition_cost: 0, salvage_value: 0, life_years: 5,
    method: "straight_line", notes: "",
  });

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/hierarchy/branches"), api.get("/hierarchy/projects")])
      .then(([b, p]) => { setBranches(b.data.items || []); setProjects(p.data.items || []); })
      .catch(() => {});
    if (initial) setF({
      name: initial.name || "", code: initial.code || "", category: initial.category || "General",
      branch_id: initial.branch_id || "", project_id: initial.project_id || "",
      acquisition_date: initial.acquisition_date || new Date().toISOString().slice(0, 10),
      acquisition_cost: initial.acquisition_cost || 0,
      salvage_value: initial.salvage_value || 0,
      life_years: initial.life_years || 5, method: initial.method || "straight_line",
      notes: initial.notes || "",
    });
    else setF((s) => ({ ...s, name: "", code: "", acquisition_cost: 0, notes: "" }));
  }, [open, initial]);

  const submit = async () => {
    if (!f.name) return toast.error("Asset name is required");
    if (Number(f.acquisition_cost) <= 0) return toast.error("Acquisition cost must be positive");
    setSaving(true);
    try {
      const body = { ...f,
        branch_id: f.branch_id || null, project_id: f.project_id || null,
        acquisition_cost: Number(f.acquisition_cost),
        salvage_value: Number(f.salvage_value),
        life_years: Number(f.life_years),
      };
      const url = initial ? `/fixed-assets/${initial.id}` : `/fixed-assets`;
      const method = initial ? "put" : "post";
      await api[method](url, body);
      toast.success(initial ? "Asset updated" : "Asset registered");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl p-4 sm:p-6 my-4 sm:my-0" onClick={(e) => e.stopPropagation()} data-testid="fa-modal">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-[#0F172A]">{initial ? `Edit ${initial.name}` : "Register Fixed Asset"}</h2>
          <button onClick={onClose} className="p-2" style={{ minWidth: 44, minHeight: 44 }}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className="label-field">Asset Name *</label><input className="input-clean" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="fa-name"/></div>
          <div><label className="label-field">Code</label><input className="input-clean" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}/></div>
          <div><label className="label-field">Category</label><input className="input-clean" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}/></div>
          <div><label className="label-field">Branch</label>
            <select className="input-clean" value={f.branch_id} onChange={(e) => setF({ ...f, branch_id: e.target.value })}>
              <option value="">— None —</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div><label className="label-field">Project</label>
            <select className="input-clean" value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })}>
              <option value="">— None —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className="label-field">Acquisition Date *</label><input type="date" className="input-clean" value={f.acquisition_date} onChange={(e) => setF({ ...f, acquisition_date: e.target.value })}/></div>
          <div><label className="label-field">Acquisition Cost *</label><input type="number" inputMode="decimal" className="input-clean" value={f.acquisition_cost} onChange={(e) => setF({ ...f, acquisition_cost: e.target.value })} data-testid="fa-cost"/></div>
          <div><label className="label-field">Salvage Value</label><input type="number" inputMode="decimal" className="input-clean" value={f.salvage_value} onChange={(e) => setF({ ...f, salvage_value: e.target.value })}/></div>
          <div><label className="label-field">Life (years) *</label><input type="number" inputMode="decimal" className="input-clean" value={f.life_years} onChange={(e) => setF({ ...f, life_years: e.target.value })}/></div>
          <div><label className="label-field">Method</label>
            <select className="input-clean" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>
              <option value="straight_line">Straight Line</option>
              <option value="wdv">Written-Down Value (WDV)</option>
            </select>
          </div>
          <div className="sm:col-span-2"><label className="label-field">Notes</label><textarea rows={2} className="input-clean" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/></div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button disabled={saving} onClick={submit} className="btn-primary w-full sm:w-auto justify-center" data-testid="fa-save">{saving ? "Saving…" : (initial ? "Save Changes" : "Register Asset")}</button>
        </div>
      </div>
    </div>
  );
}


export default function FixedAssets() {
  const { active } = useCompany();
  const { role } = useAuth();
  const cur = active?.base_currency || "INR";
  const [data, setData] = useState({ items: [], total_book_value: 0, total_acquisition_cost: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [running, setRunning] = useState(false);
  const canWrite = ["owner", "admin", "super_admin"].includes(role);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/fixed-assets"); setData(data); }
    catch { setData({ items: [], total_book_value: 0, total_acquisition_cost: 0 }); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (active) load(); }, [active]); // eslint-disable-line

  const runDep = async () => {
    if (!window.confirm("Post depreciation up to today for every active asset?")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/fixed-assets/run-depreciation");
      toast.success(`${data.posted} monthly depreciation entries posted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };

  const dispose = async (a) => {
    if (!window.confirm(`Dispose ${a.name}? This stops future depreciation.`)) return;
    try { await api.post(`/fixed-assets/${a.id}/dispose`); toast.success("Asset disposed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totalAccDep = useMemo(() => data.total_acquisition_cost - data.total_book_value, [data]);

  return (
    <div className="space-y-4" data-testid="fixed-assets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Fixed Assets</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Landmark size={22} className="text-[#FF5500]"/> Asset Register
          </h1>
          <div className="text-sm text-[#64748B] mt-1">Track capital assets, depreciate straight-line or WDV, post journal vouchers automatically.</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {canWrite && <button onClick={runDep} disabled={running} className="btn-secondary w-full sm:w-auto justify-center" data-testid="fa-run-dep">
            <Play size={14}/> {running ? "Posting…" : "Run Monthly Depreciation"}
          </button>}
          {canWrite && <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary w-full sm:w-auto justify-center" data-testid="new-fa-btn">
            <Plus size={14}/> New Asset
          </button>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card-surface p-3"><div className="overline">Acquisition cost</div><div className="text-xl sm:text-2xl font-bold font-mono-data text-[#0F172A]" data-testid="fa-total-cost">{fmt(data.total_acquisition_cost, cur)}</div></div>
        <div className="card-surface p-3"><div className="overline">Accumulated depreciation</div><div className="text-xl sm:text-2xl font-bold font-mono-data text-[#DC2626]" data-testid="fa-total-dep">{fmt(totalAccDep, cur)}</div></div>
        <div className="card-surface p-3"><div className="overline">Net book value</div><div className="text-xl sm:text-2xl font-bold font-mono-data text-[#FF5500]" data-testid="fa-total-book">{fmt(data.total_book_value, cur)}</div></div>
      </div>

      <div className="card-surface p-3 sm:p-4">
        {loading ? <div className="py-6 text-center text-[#64748B]">Loading…</div>
         : data.items.length === 0 ? <div className="py-8 text-center text-[#64748B]" data-testid="fa-empty">No assets registered yet.</div>
         : (<div className="table-scroll"><table className="table-clean">
          <thead><tr><th>Name</th><th>Category</th><th>Method</th><th className="text-right">Cost</th><th className="text-right">Acc. Dep.</th><th className="text-right">Book Value</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {data.items.map((a) => (
              <tr key={a.id} data-testid={`fa-row-${a.id}`}>
                <td className="font-medium">{a.name}<div className="text-[10px] text-[#64748B] font-mono-data">{a.code}</div></td>
                <td className="text-xs">{a.category}</td>
                <td className="text-xs font-mono-data">{a.method}</td>
                <td className="text-right font-mono-data">{fmt(a.acquisition_cost, cur)}</td>
                <td className="text-right font-mono-data text-[#DC2626]">{fmt(a.accumulated_depreciation, cur)}</td>
                <td className="text-right font-mono-data font-semibold">{fmt(a.book_value, cur)}</td>
                <td><span className={`chip ${a.status === "active" ? "chip-success" : a.status === "disposed" ? "chip-danger" : "chip-warn"}`}>{a.status}</span></td>
                <td>
                  <div className="flex gap-2 justify-end">
                    {canWrite && a.accumulated_depreciation === 0 && a.status === "active" && (
                      <button onClick={() => { setEditing(a); setOpen(true); }} className="text-[#64748B] hover:text-[#FF5500]"><Edit3 size={14}/></button>
                    )}
                    {canWrite && a.status === "active" && (
                      <button onClick={() => dispose(a)} className="text-[#DC2626]" title="Dispose asset"><TrendingDown size={14}/></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>)}
      </div>

      <AssetModal open={open} initial={editing} onClose={() => setOpen(false)} onSaved={load}/>
    </div>
  );
}
